import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { Anchor, Phone } from "lucide-react";
import { IMAGES } from "@/lib/api";
import Home from "@/pages/Home";
import BookingPage from "@/pages/BookingPage";
import ManageBooking from "@/pages/ManageBooking";
import AdminPage from "@/pages/AdminPage";
import PartnerPage from "@/pages/PartnerPage";
import ResetPassword from "@/pages/ResetPassword";
import Gallery from "@/pages/Gallery";

const Nav = () => {
  const { pathname } = useLocation();
  const link = (to, label, testid) => (
    <Link
      to={to}
      data-testid={testid}
      className={`text-xs sm:text-sm font-semibold tracking-wide px-1.5 sm:px-3 py-2 rounded-full whitespace-nowrap transition-colors duration-150 ${
        pathname === to ? "text-cyan-300 bg-cyan-400/10" : "text-slate-200 hover:text-cyan-300"
      }`}
    >
      {label}
    </Link>
  );
  return (
    <nav className="glass-nav fixed top-0 inset-x-0 z-50">
      <div className="max-w-6xl mx-auto px-2 sm:px-8 h-16 flex items-center justify-between">
        <Link to="/" data-testid="nav-brand-logo" className="flex items-center gap-3">
          <img src={IMAGES.logo} alt="Mickey Mouse Charters" className="h-10 w-14 object-cover rounded-md hidden sm:block" />
          <span className="font-display font-extrabold text-white text-lg tracking-tight hidden sm:block">
            Mickey Mouse <span className="text-cyan-400">Charters</span>
          </span>
        </Link>
        <div className="flex items-center gap-1 sm:gap-2">
          {link("/", "Home", "nav-home-link")}
          {link("/gallery", "Gallery", "nav-gallery-link")}
          {link("/manage", "Manage Booking", "nav-manage-booking-link")}
          <Link
            to="/#contact"
            data-testid="nav-contact-link"
            className="text-xs sm:text-sm font-semibold tracking-wide px-1.5 sm:px-3 py-2 rounded-full whitespace-nowrap transition-colors duration-150 text-slate-200 hover:text-cyan-300"
          >
            Contact
          </Link>
          <Link
            to="/book"
            data-testid="nav-book-now-btn"
            className="btn-coral text-white text-xs sm:text-sm font-bold px-2.5 sm:px-5 py-2 rounded-full ml-0.5 sm:ml-1 whitespace-nowrap"
          >
            Book Now
          </Link>
        </div>
      </div>
    </nav>
  );
};

const Footer = () => (
  <footer className="bg-black text-slate-300 mt-0">
    <div className="max-w-6xl mx-auto px-4 sm:px-8 py-14 grid grid-cols-1 md:grid-cols-3 gap-10">
      <div>
        <img src={IMAGES.logo} alt="Mickey Mouse Charters" className="h-20 rounded-lg mb-4" />
        <p className="text-sm text-slate-400 leading-relaxed">
          Watersports charters in the beautiful Cayman Islands. Party cruises, stingray encounters and beach getaways.
        </p>
      </div>
      <div>
        <h4 className="font-display font-bold text-white mb-3">Contact</h4>
        <p className="text-sm flex items-center gap-2 mb-2">
          <Phone size={14} className="text-cyan-400" /> Tel: 1 (345) 916-1163
        </p>
        <p className="text-sm flex items-center gap-2">
          <Anchor size={14} className="text-cyan-400" /> Camana Bay Marina · Royal Watler Cruise Terminal
        </p>
      </div>
      <div>
        <h4 className="font-display font-bold text-white mb-3">Quick Links</h4>
        <ul className="space-y-2 text-sm">
          <li><Link to="/book" className="hover:text-cyan-300" data-testid="footer-book-link">Book a Tour</Link></li>
          <li><Link to="/manage" className="hover:text-cyan-300" data-testid="footer-manage-link">Manage / Cancel Booking</Link></li>
          <li><Link to="/partner" className="hover:text-cyan-300" data-testid="nav-partner-link">Partner Portal</Link></li>
          <li><Link to="/admin" className="hover:text-cyan-300" data-testid="nav-admin-link">Admin</Link></li>
        </ul>
      </div>
    </div>
    <div className="border-t border-slate-800 py-4 text-center text-xs text-slate-500">
      © {new Date().getFullYear()} Mickey Mouse Charters · Cayman Islands
    </div>
  </footer>
);

function App() {
  return (
    <BrowserRouter>
      <Nav />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/book" element={<BookingPage />} />
        <Route path="/manage" element={<ManageBooking />} />
        <Route path="/admin" element={<AdminPage />} />
        <Route path="/partner" element={<PartnerPage />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/gallery" element={<Gallery />} />
      </Routes>
      <Footer />
      <Toaster position="top-center" richColors />
    </BrowserRouter>
  );
}

export default App;
